/**
 * Chocolate Explorer – Flavor Finder
 * Premium interactive quiz matching customers with their perfect chocolate.
 * Simply Prestige · 2026
 *
 * Flow:
 *   Default path: Q1 (occasion) -> Q2 (flavor) -> results
 *   Gift path:    Q1 (gift)     -> budget     -> Q2 (flavor) -> results
 *
 * Weighted tag scoring: Q2 (flavor) tags count double.
 * Personality-based result headings from data-profile attributes.
 * Add to Cart via Shopify AJAX API.
 */

class FlavorFinder extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 'intro';
    this.totalQuestions = 2;
    this.answers = {};
    this.collectedTags = [];
    this.filterGroups = [];
    this.profile = {};
    this.isGiftPath = false;
    this.budgetRange = null;
  }

  connectedCallback() {
    this.collection = this.dataset.collection;

    /* Element references */
    this.introScreen    = this.querySelector('[data-step="intro"]');
    this.stepsIndicator = this.querySelector('.ff__steps');
    this.backBtn        = this.querySelector('.ff__back');
    this.resultsEl      = this.querySelector('.ff__results');
    this.loadingEl      = this.querySelector('.ff__loading');
    this.resultsGrid    = this.querySelector('.ff__results-grid');
    this.profileTitle   = this.querySelector('[data-profile-title]');
    this.profileDesc    = this.querySelector('[data-profile-desc]');
    this.stepLabel      = this.querySelector('[data-step-label]');
    this.dots           = this.querySelectorAll('.ff__dot');
    this.budgetDot      = this.querySelector('.ff__dot--budget');
    this.budgetDotLine  = this.querySelector('.ff__dot-line--budget');

    this.bindEvents();
  }

  /* ─── Events ─── */

  bindEvents() {
    const startBtn = this.querySelector('.ff__start-btn');
    if (startBtn) startBtn.addEventListener('click', () => this.startQuiz());

    this.querySelectorAll('.ff__card').forEach(card => {
      card.addEventListener('click', e => this.handleCardClick(e));
    });

    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.goBack());
    }

    const restartBtn = this.querySelector('.ff__restart');
    if (restartBtn) restartBtn.addEventListener('click', () => this.restart());
  }

  /* ─── Quiz flow ─── */

  startQuiz() {
    this.introScreen.dataset.active = 'false';
    setTimeout(() => {
      this.introScreen.hidden = true;
      this.stepsIndicator.hidden = false;
      this.backBtn.hidden = false;
      this.goToStep(1);
    }, 300);
  }

  handleCardClick(e) {
    const card     = e.currentTarget;
    const question = card.closest('.ff__question');
    const step     = question.dataset.step;
    const stepNum  = parseInt(step);
    const tags     = card.dataset.tags ? card.dataset.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
    const weight   = parseInt(card.dataset.weight) || 1;
    const profile  = card.dataset.profile;

    /* Visual feedback */
    question.querySelectorAll('.ff__card').forEach(c => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    /* Store answer */
    if (step === 'budget') {
      this.budgetRange = {
        min: parseFloat(card.dataset.priceMin) || 0,
        max: parseFloat(card.dataset.priceMax) || Infinity,
      };
    } else {
      const isFilter = card.dataset.filter === 'true';
      this.answers[step] = { tags, weight, profile, isFilter };
      this.profile[stepNum] = profile;
    }

    /* Advance — gift path inserts budget step between Q1 and Q2 */
    setTimeout(() => {
      if (step === '1' && profile === 'gift') {
        this.isGiftPath = true;
        this.showBudgetDot(true);
        this.goToStep('budget');
      } else if (step === '1') {
        this.isGiftPath = false;
        this.budgetRange = null;
        this.showBudgetDot(false);
        this.goToStep(2);
      } else if (step === 'budget') {
        this.goToStep(2);
      } else if (stepNum < this.totalQuestions) {
        this.goToStep(stepNum + 1);
      } else {
        /* Q2 is the final step — advance to results */
        this.showResults();
      }
    }, 350);
  }

  goToStep(number) {
    /* Hide all questions */
    this.querySelectorAll('.ff__question').forEach(q => {
      q.dataset.active = 'false';
    });

    /* Show target */
    const target = this.querySelector(`.ff__question[data-step="${number}"]`);
    if (target) {
      /* Small delay so the exit animation plays first */
      requestAnimationFrame(() => {
        target.dataset.active = 'true';
        if (this.stepLabel) this.stepLabel.textContent = target.dataset.stepName || '';
      });
    }

    this.currentStep = number;
    this.updateDots();
  }

  goBack() {
    const step = this.currentStep;
    const stepNum = parseInt(step);

    /* From budget -> back to step 1 */
    if (step === 'budget') {
      this.budgetRange = null;
      this.isGiftPath = false;
      this.showBudgetDot(false);
      this.goToStep(1);
      return;
    }

    /* From step 2 on gift path -> back to budget */
    if (stepNum === 2 && this.isGiftPath) {
      delete this.answers[2];
      delete this.profile[2];
      this.goToStep('budget');
      return;
    }

    if (isNaN(stepNum) || stepNum <= 1) {
      /* Back to intro */
      this.querySelectorAll('.ff__question').forEach(q => { q.dataset.active = 'false'; });
      this.stepsIndicator.hidden = true;
      this.backBtn.hidden = true;
      this.introScreen.hidden = false;
      requestAnimationFrame(() => { this.introScreen.dataset.active = 'true'; });
      this.currentStep = 'intro';
      return;
    }

    /* Clear current step answer */
    delete this.answers[stepNum];
    delete this.profile[stepNum];
    this.goToStep(stepNum - 1);
  }

  updateDots() {
    const step = this.currentStep;
    const stepNum = parseInt(step) || 0;
    const onBudget = (step === 'budget');

    this.dots.forEach(dot => {
      const dotId = dot.dataset.dot;
      if (dotId === 'budget') {
        dot.classList.toggle('is-active', onBudget);
        dot.classList.toggle('is-done', !onBudget && stepNum >= 2);
      } else {
        const n = parseInt(dotId);
        dot.classList.toggle('is-active', !onBudget && n === stepNum);
        dot.classList.toggle('is-done', onBudget ? n <= 1 : n < stepNum);
      }
    });
  }

  showBudgetDot(show) {
    if (this.budgetDot) this.budgetDot.hidden = !show;
    if (this.budgetDotLine) this.budgetDotLine.hidden = !show;
  }

  /* ─── Results ─── */

  async showResults() {
    /* Hide questions + navigation */
    this.querySelectorAll('.ff__question').forEach(q => { q.dataset.active = 'false'; });
    this.stepsIndicator.hidden = true;
    this.backBtn.hidden = true;

    /* Show loading */
    this.loadingEl.hidden = false;

    /* Build weighted tag list + hard-filter groups */
    this.collectedTags = [];
    this.filterGroups = [];

    Object.values(this.answers).forEach(a => {
      if (a.isFilter && a.tags.length > 0) {
        /* Filter options: products MUST match >=1 tag from each group */
        this.filterGroups.push(a.tags.map(t => t.toLowerCase()));
      }
      /* ALL tags contribute to scoring (including filter tags) */
      a.tags.forEach(tag => {
        for (let i = 0; i < a.weight; i++) {
          this.collectedTags.push(tag.toLowerCase());
        }
      });
    });

    try {
      const products = await this.fetchProducts();
      const matched  = this.matchProducts(products);

      this.setProfileText();
      this.renderResults(matched);

      /* Nice loading feel */
      await this.delay(1400);

      this.loadingEl.hidden = true;
      this.resultsEl.hidden = false;
    } catch (err) {
      console.error('Chocolate Explorer error:', err);
      await this.delay(800);
      this.loadingEl.hidden = true;
      this.resultsEl.hidden = false;
      const L = (window.SC_FF_LABELS || {});
      this.resultsGrid.innerHTML = `<p class="ff__error">${L.fetchError || 'Kunne ikke hente produkter. Prøv igen senere.'}</p>`;
    }
  }

  setProfileText() {
    /* Title now keyed on flavor only (Q3 mood removed) */
    const flavor = this.profile[2] || 'dark';
    const occasion = this.profile[1] || 'solo';
    const L = (window.SC_FF_LABELS || {});

    const titles = {
      dark:  (L.profiles && L.profiles.dark)  || 'Den Dybe Nyder',
      milk:  (L.profiles && L.profiles.milk)  || 'Den Klassiske Hyggefan',
      fruit: (L.profiles && L.profiles.fruit) || 'Den Friske Drømmer',
      nuts:  (L.profiles && L.profiles.nuts)  || 'Den Varme Nøddekender',
      spicy: (L.profiles && L.profiles.spicy) || 'Den Modige Smagsekspert',
    };

    const descs = {
      solo:    (L.occasions && L.occasions.solo)    || 'Vi har fundet de perfekte chokolader til din personlige forkælelse:',
      gift:    (L.occasions && L.occasions.gift)    || 'Her er vores bedste gaveanbefalinger til dig:',
      share:   (L.occasions && L.occasions.share)   || 'Perfekte valg til at dele med dem du holder af:',
      protein: (L.occasions && L.occasions.protein) || 'Vores bedste protein- og energivalg til dig:',
    };

    if (this.profileTitle) this.profileTitle.textContent = titles[flavor] || L.fallbackTitle || 'Din perfekte match';
    if (this.profileDesc)  this.profileDesc.textContent  = descs[occasion] || L.fallbackDesc || 'Baseret på dine svar anbefaler vi disse chokolader:';
  }

  /* ─── Product fetching & matching ─── */

  async fetchProducts() {
    const url = this.collection
      ? `/collections/${this.collection}/products.json?limit=250`
      : '/products.json?limit=250';

    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.products;
  }

  matchProducts(products) {
    const scored = products.map(product => {
      const ptags = product.tags.map(t => t.toLowerCase().trim());
      let score = 0;

      this.collectedTags.forEach(tag => {
        if (ptags.includes(tag)) score++;
      });

      /* Filter-bonus: products matching MORE filter-group tags rank higher.
         E.g. protein bar with 5 protein tags -> 500 bonus vs gift box with 1 -> 100 */
      let filterBonus = 0;
      this.filterGroups.forEach(group => {
        group.forEach(ft => {
          if (ptags.includes(ft)) filterBonus++;
        });
      });

      return { ...product, matchScore: score + (filterBonus * 100) };
    });

    let results = scored.filter(p => p.matchScore > 0);

    /* Hard filters — product must match >=1 tag from EACH filter group */
    if (this.filterGroups.length > 0) {
      const filtered = results.filter(p => {
        const ptags = p.tags.map(t => t.toLowerCase().trim());
        return this.filterGroups.every(group =>
          group.some(ft => ptags.includes(ft))
        );
      });
      /* Fallback: if every product is filtered out, show unfiltered */
      if (filtered.length > 0) results = filtered;
    }

    /* Price budget filter (gift path) */
    if (this.budgetRange) {
      const { min, max } = this.budgetRange;
      const priceFiltered = results.filter(p => {
        const price = parseFloat(p.variants[0].price);
        return price >= min && price <= max;
      });
      if (priceFiltered.length > 0) results = priceFiltered;
    }

    return results
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 3);
  }

  /* ─── Render ─── */

  renderResults(products) {
    if (!products.length) {
      const L = (window.SC_FF_LABELS || {});
      this.resultsGrid.innerHTML = `
        <p class="ff__error">
          ${L.noResults || 'Vi fandt ingen produkter der matcher dine præferencer.'}
          <a href="/collections/all">${L.seeAll || 'Se alle produkter'}</a>
        </p>`;
      return;
    }

    this._maxScore = products[0]?.matchScore || 1;
    this.resultsGrid.innerHTML = products.map((p, i) => this.renderCard(p, i)).join('');

    /* Stagger card entrance */
    this.resultsGrid.querySelectorAll('.ff__product').forEach((card, i) => {
      card.style.animationDelay = `${i * 0.15}s`;
    });
  }

  renderCard(product, index) {
    const L = (window.SC_FF_LABELS || {});
    const img = product.images[0] ? product.images[0].src : '';
    const price = this.formatMoney(product.variants[0].price);
    /* Relative scoring: top match ~97%, others proportional (range ~67-97%) */
    const matchPct = this._maxScore > 0
      ? Math.min(97, Math.round((product.matchScore / this._maxScore) * 30 + 67))
      : 80;
    const variantId = product.variants[0].id;

    return `
      <div class="ff__product" style="animation-delay:${index * 0.15}s">
        <a href="/products/${product.handle}" class="ff__product-link">
          ${img
            ? `<img src="${img}" alt="${this.escapeHtml(product.title)}" class="ff__product-image" loading="lazy" width="400" height="400">`
            : '<div class="ff__product-image ff__product-image--placeholder"></div>'}
          <div class="ff__product-info">
            <h4 class="ff__product-title">${this.escapeHtml(product.title)}</h4>
            <p class="ff__product-price">${price}</p>
            <span class="ff__product-match">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M20 6L9 17l-5-5"/></svg>
              ${matchPct}% ${L.matchLabel || 'match'}
            </span>
          </div>
        </a>
        <button type="button" class="ff__atc button" data-variant-id="${variantId}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
          <span>${L.addToCart || 'Læg i kurv'}</span>
        </button>
      </div>`;
  }

  /* ─── Utils ─── */

  formatMoney(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return '';
    const L = (window.SC_FF_LABELS || {});
    const code = (L && L.currencyCode) || 'DKK';
    return num.toFixed(2).replace('.', ',') + ' ' + code;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  delay(ms) {
    return new Promise(r => setTimeout(r, ms));
  }

  /* ─── Restart ─── */

  restart() {
    this.currentStep = 'intro';
    this.answers = {};
    this.collectedTags = [];
    this.filterGroups = [];
    this.profile = {};
    this.isGiftPath = false;
    this.budgetRange = null;
    this.showBudgetDot(false);

    this.querySelectorAll('.ff__card').forEach(c => c.classList.remove('is-selected'));
    this.querySelectorAll('.ff__question').forEach(q => { q.dataset.active = 'false'; });

    this.resultsEl.hidden = true;
    this.resultsGrid.innerHTML = '';
    this.stepsIndicator.hidden = true;
    this.backBtn.hidden = true;
    this.introScreen.hidden = false;

    requestAnimationFrame(() => { this.introScreen.dataset.active = 'true'; });
  }
}

/* ─── Add to Cart (event delegation — mirrors Prestige theme ProductForm flow) ─── */

document.addEventListener('click', async e => {
  const btn = e.target.closest('.ff__atc');
  if (!btn) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const L = (window.SC_FF_LABELS || {});
  const label = btn.querySelector('span');
  btn.disabled = true;
  label.textContent = L.adding || 'Tilføjer...';

  /* Theme loading indicator */
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  /* Collect sections to bundle (for cart drawer re-rendering) */
  const sectionsToBundle = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

  try {
    /* Build FormData exactly like Prestige's <product-form> */
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', sectionsToBundle.join(','));
    formData.append('sections_url', `${Shopify.routes.root}variants/${variantId}`);

    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: formData,
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    if (!response.ok) throw new Error('Cart error');
    const responseJson = await response.json();

    /* If theme uses page cart instead of drawer, redirect */
    if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }

    /* Fetch full cart content and merge bundled section HTML */
    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent['sections'] = responseJson['sections'];

    /* Dispatch cart:change — this opens the cart drawer + updates count/dot */
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        cart: cartContent,
      },
    }));

    label.textContent = L.added || 'Tilføjet ✓';
    btn.classList.add('is-added');

    setTimeout(() => {
      btn.disabled = false;
      btn.classList.remove('is-added');
      label.textContent = L.addToCart || 'Læg i kurv';
    }, 2500);
  } catch (err) {
    console.error('ATC error:', err);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    label.textContent = L.errorRetry || 'Fejl – prøv igen';
    setTimeout(() => {
      btn.disabled = false;
      label.textContent = L.addToCart || 'Læg i kurv';
    }, 2000);
  }
});

/* ─── Register ─── */

if (!customElements.get('flavor-finder')) {
  customElements.define('flavor-finder', FlavorFinder);
}
